"use client";

import { c as _c } from "react/compiler-runtime";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { MonitorSmartphone, HelpCircle, Plus } from "lucide-react";
import Link from "next/link";
import React, { useId, useRef } from "react";
import { Button } from "@/lib/components/button";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "@/lib/components/tooltip";
import Logo from "@/lib/icons/Logo";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { ProviderIcons } from "@llmgateway/shared/components";
import { AnimatedBeam } from "./animated-beam";
const requestModelHref = `mailto:dotebceo@gmail.com?subject=${encodeURIComponent("[Model Request] ")}`;
const Circle = t0 => {
  const $ = _c(6);
  const {
    ref,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("group relative z-10 flex size-14 items-center justify-center rounded-full border-2 bg-white p-3 shadow-lg shadow-black/5 dark:shadow-black/20 backdrop-blur-sm dark:bg-black dark:border-neutral-800", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== ref || $[4] !== t1) {
    t2 = <div ref={ref} className={t1}>{children}</div>;
    $[2] = children;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
Circle.displayName = "Circle";
export function Graph() {
  const {
    t
  } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const rightRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
  const OpenAIIcon = ProviderIcons.openai;
  const AnthropicIcon = ProviderIcons.anthropic;
  const XAIIcon = ProviderIcons.xai;
  const DeepseekIcon = ProviderIcons.deepseek;
  const logos = [<OpenAIIcon key={useId()} className="w-6 h-6 object-contain" />, <AnthropicIcon key={useId()} className="w-6 h-6 object-contain" />, <XAIIcon key={useId()} className="w-6 h-6 object-contain" />, <DotsHorizontalIcon key={useId()} />, <DeepseekIcon key={useId()} className="w-6 h-6 object-contain" />];
  const stats = [{
    value: "40+",
    label: t("landing.graph.stats.providers")
  }, {
    value: "400+",
    label: t("landing.graph.stats.models")
  }, {
    value: "100B+",
    label: t("landing.graph.stats.tokens")
  }];
  return <div className="relative w-full py-28 md:py-36 px-4 overflow-hidden">
			{/* Gradient background */}
			<div className="absolute inset-0 bg-gradient-to-b from-background via-surface-elevated to-background" />
			{/* Noise texture */}
			<div className="absolute inset-0 bg-noise" />

			<div className="relative">
				{/* Header */}
				<div className="container mx-auto px-4">
					<div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-4">
						<div>
							<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4">
								{t("landing.graph.eyebrow")}
							</p>
							<h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-foreground">
								{t("landing.graph.title")}
							</h2>
							<p className="mt-4 text-muted-foreground max-w-xl">
								{t("landing.graph.description")}
							</p>
						</div>
						<div className="flex gap-8 lg:gap-12">
							{stats.map(stat => <div key={stat.label}>
									<div className="font-display text-3xl md:text-4xl font-bold tabular-nums bg-gradient-to-b from-foreground to-foreground/60 bg-clip-text text-transparent">
										{stat.value}
									</div>
									<div className="text-sm text-muted-foreground">
										{stat.label}
									</div>
								</div>)}
						</div>
					</div>
				</div>

				{/* Diagram */}
				<div className="relative mx-auto max-w-4xl">
					{/* Faint radial glow behind center */}
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/5 dark:bg-blue-400/5 rounded-full blur-3xl pointer-events-none" />

					<div className="relative flex h-[500px] items-center justify-center p-10" ref={containerRef}>
						<div className="absolute left-10 top-1/2 -translate-y-1/2 z-10">
							<Circle ref={leftRef}>
								<MonitorSmartphone className="text-black dark:text-white" />
							</Circle>
						</div>

						<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
							<Circle ref={centerRef}>
								<Logo />
							</Circle>
						</div>

						<div className="absolute right-10 top-1/2 flex -translate-y-1/2 flex-col items-center justify-center gap-6 z-10">
							{logos.map((LogoIcon, index) => <Circle key={index} ref={rightRefs[index]}>
									{LogoIcon}
								</Circle>)}

							<div className="relative group">
								<TooltipProvider delayDuration={100}>
									<Tooltip>
										<TooltipTrigger>
											<Circle ref={rightRefs[6]}>
												<HelpCircle className="text-muted-foreground dark:text-neutral-400" />
											</Circle>
										</TooltipTrigger>
										<TooltipContent>
											{t("landing.graph.tooltipPrefix")}{" "}
											<a href="mailto:contact@doteb.com" className="text-blue-500 underline" target="_blank" rel="noreferrer noopener">
												{t("landing.graph.tooltipLink")}
											</a>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
						</div>

						<AnimatedBeam containerRef={containerRef} fromRef={leftRef} toRef={centerRef} />
						{rightRefs.map((ref, i) => <AnimatedBeam key={i} containerRef={containerRef} fromRef={centerRef} toRef={ref} curvature={(i - 2.5) * 20} />)}
					</div>
				</div>

				<div className="flex justify-center space-x-6">
					<Button asChild>
						<Link href="/models" prefetch={true}>
							{t("landing.graph.viewAllModels")}
						</Link>
					</Button>
					<Button variant="outline" asChild>
						<a href={requestModelHref} className="inline-flex items-center gap-2">
							<Plus className="h-4 w-4" />
							{t("landing.graph.requestModel")}
						</a>
					</Button>
				</div>
			</div>
		</div>;
}